import { Formik } from 'formik';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import PhoneInput, { getCountryCallingCode } from 'react-phone-number-input';
import styled, { useTheme } from 'styled-components';
import { Checkbox } from 'components/Form/Checkbox';
import Loading from 'components/Loading';
import { POSModal, POSButton } from 'components/POS';
import Spacer from 'components/Spacer';
import Toaster from 'components/Toaster';
import { AppContext } from 'shared/AppContext';
import { useCart } from 'shared/CartContext';
import { useMobileDelivery } from './gql/queries/useMobileDelivery';
import { useTextReceipt } from './gql/queries/useTextReceipt';
import { successMessage } from './PaymentOption';

const formValues = {
  cellphone: '',
  country_code: '',
  calling_code: '',
  send_receipt: true,
};

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: auto;
  align-items: center;
  width: 100%;
  gap: 16px;
`;

const PhoneInputContainer = styled.div`
  width: 100%;

  & .PhoneInput {
    background: transparent;
    box-sizing: border-box;

    & .PhoneInputInput {
      color: ${(props) =>
        props.theme.name === 'dark'
          ? props.theme.colors?.text?.primary || '#ffffff'
          : '#000033'};
      background: ${(props) =>
        props.theme.name === 'dark'
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0, 0, 0, 0.03)'};
      flex: 1 1;
      outline: none;
      min-width: 0;
      font-family: 'Barlow Condensed', sans-serif;
      font-weight: 600;
      border: 2px solid
        ${(props) =>
          props.theme.name === 'dark'
            ? 'rgba(255, 255, 255, 0.15)'
            : 'rgba(0, 0, 0, 0.15)'};
      border-radius: 10px;
      margin: 6px;
      padding: 14px;
      font-size: 28px;
      text-align: center;
      max-width: 100%;
      transition: border-color 0.2s ease;

      &:focus {
        border-color: ${(props) => props.theme.colors?.primary || '#FA4616'};
      }

      @media (max-width: 450px) {
        min-width: 200px;
        font-size: 24px;
        padding: 12px;
      }
    }

    & .PhoneInputInput::placeholder {
      word-spacing: -3px;
      letter-spacing: 3px;
      font-size: 26px;
      font-weight: 600;
      color: ${(props) =>
        props.theme.name === 'dark'
          ? 'rgba(255, 255, 255, 0.3)'
          : 'rgba(0, 0, 0, 0.3)'};

      @media (max-width: 450px) {
        font-size: 22px;
      }
    }

    .PhoneInputCountry {
      .PhoneInputCountrySelectArrow {
        color: ${(props) => props.theme.colors?.primary || '#FA4616'};
        border-right-width: 3px;
        border-bottom-width: 3px;
      }
    }
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  justify-content: start;
  padding-left: 10px;
  width: 100%;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  width: 100%;
  margin-top: auto;
`;

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 16px;
`;

const MobileDeliveryModal = ({
  isVisible,
  setIsVisible,
  printReceipt = false,
  showSuccessMessage,
}) => {
  const theme = useTheme();

  const { clearTicket, purchaseId, event, account: eventAccount } = useCart();

  // Toast state
  const [toastState, setToastState] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: '',
  });

  const showToast = ({ type, title, message }) => {
    setToastState({ isOpen: true, type, title, message });
  };

  const closeToast = () => {
    setToastState((prev) => ({ ...prev, isOpen: false }));
  };

  const {
    state: { account },
  } = useContext(AppContext);

  const availableAccount = account || eventAccount;

  const posSettings =
    event && event?.pos_settings
      ? event?.pos_settings
      : availableAccount && availableAccount?.pos_settings
      ? availableAccount?.pos_settings
      : undefined;

  const showMobilePrint = posSettings
    ? posSettings?.ticket_delivery === 'mobile'
    : false;
  const showTextReceipt = posSettings
    ? posSettings?.receipt_delivery === 'mobile'
    : false;

  const [isPrintReceipt, setIsPrintReceipt] = useState(printReceipt);
  const [printReceiptSelected, setPrintReceiptSelected] = useState(true);

  const mobileDelivery = useMobileDelivery();
  const textReceipt = useTextReceipt();

  const resetAndClose = () => {
    setTimeout(() => {
      clearTicket();

      setIsVisible(false);
      setIsPrintReceipt(false);
    }, 1000);
  };

  useEffect(() => {
    if (showSuccessMessage && isVisible) {
      successMessage();
    }
  }, [showSuccessMessage, isVisible]);

  useEffect(() => {
    if (!isVisible) {
      setIsPrintReceipt(false);
      setPrintReceiptSelected(false);
    }
  }, [isVisible]);

  useEffect(() => {
    if (isVisible && !showMobilePrint && showTextReceipt)
      setIsPrintReceipt(true);
    else if (isVisible && !showMobilePrint && !showTextReceipt) resetAndClose();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, showMobilePrint, showTextReceipt]);

  const modalTitle = isPrintReceipt ? 'Text Receipt' : 'Mobile Ticket Delivery';

  const content =
    showMobilePrint || showTextReceipt ? (
      <POSModal
        isVisible={isVisible}
        onClose={resetAndClose}
        title={modalTitle}
      >
        {showMobilePrint || showTextReceipt ? (
          <Formik
            initialValues={formValues}
            validate={(values) => {
              const errors = {};

              if (!values.cellphone) {
                errors.cellphone = 'Required';
              }

              return errors;
            }}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              if (!values.cellphone) return;

              setSubmitting(true);
              try {
                const result = !isPrintReceipt
                  ? mobileDelivery({
                      cellphone: values.cellphone,
                      calling_code: values.calling_code ?? '+1',
                      purchase_id: +purchaseId?.id,
                      text_receipt: values.send_receipt,
                    })
                  : textReceipt({
                      cellphone: values.cellphone,
                      calling_code: values.calling_code ?? '+1',
                      purchase_id: +purchaseId?.id,
                    });

                if (result && values.send_receipt) {
                  showToast({
                    type: 'success',
                    title: 'Sent Successfully',
                    message: 'Your tickets have been delivered.',
                  });
                  resetForm();
                  resetAndClose();
                } else if (result) {
                  showToast({
                    type: 'success',
                    title: 'Sent Successfully',
                    message: 'Your tickets have been delivered.',
                  });
                  resetForm();
                  setIsPrintReceipt(true);
                }
              } catch (e) {
                console.error(e);
                showToast({
                  type: 'error',
                  title: 'Failed to Send',
                  message: 'Please try again.',
                });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({
              values,
              errors,
              handleBlur,
              handleSubmit,
              setFieldValue,
              isSubmitting,
            }) => {
              return (
                <form
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                  }}
                  onSubmit={handleSubmit}
                >
                  <BodyWrapper>
                    <FormContainer>
                      <PhoneInputContainer theme={theme}>
                        <PhoneInput
                          id="cellphone"
                          placeholder="___ ___ ____"
                          size={25}
                          onBlur={handleBlur}
                          defaultCountry={'US'}
                          onChange={(value) => {
                            setFieldValue('cellphone', value ?? '');
                          }}
                          onCountryChange={(e) => {
                            if (e) {
                              setFieldValue('country_code', e ?? '');
                              setFieldValue(
                                'calling_code',
                                getCountryCallingCode(e) ?? ''
                              );
                            }
                          }}
                          value={values.cellphone}
                          error={errors.cellphone}
                        />
                      </PhoneInputContainer>

                      {!isPrintReceipt && (
                        <CheckboxWrapper>
                          <Checkbox
                            name="checkbox1"
                            checkedIcon={'check1'}
                            checkedBgColor={'#1dbe1d'}
                            checked={values?.send_receipt || false}
                            onChange={() => {
                              setFieldValue(
                                'send_receipt',
                                !values?.send_receipt ?? false
                              );

                              setPrintReceiptSelected(!values?.send_receipt);
                            }}
                            size={[32, 32, 8]}
                            check={values?.send_receipt || false}
                            rightText={'Send Receipt'}
                            myStyle={{ cursor: 'pointer' }}
                            textProps={{
                              style: {
                                fontFamily: "'Barlow Condensed', sans-serif",
                                fontSize: '20px',
                                fontWeight: '600',
                                color:
                                  theme?.name === 'dark'
                                    ? '#ffffff'
                                    : '#000033',
                              },
                            }}
                          />
                        </CheckboxWrapper>
                      )}
                    </FormContainer>

                    <ButtonWrapper>
                      {showTextReceipt && !printReceiptSelected && (
                        <POSButton
                          variant="outlined"
                          style={{
                            flex: 1,
                            padding: '14px 20px',
                            fontSize: '18px',
                          }}
                          onClick={() =>
                            isPrintReceipt || printReceiptSelected
                              ? resetAndClose()
                              : setIsPrintReceipt(true)
                          }
                        >
                          {isPrintReceipt ? 'New Transaction' : 'Skip'}
                        </POSButton>
                      )}

                      <Spacer size={5} />

                      <POSButton
                        type="submit"
                        variant="primary"
                        style={{
                          flex: 1,
                          padding: '16px 20px',
                          fontSize: '18px',
                        }}
                        disabled={isSubmitting}
                      >
                        Send
                      </POSButton>
                    </ButtonWrapper>
                  </BodyWrapper>
                </form>
              );
            }}
          </Formik>
        ) : (
          <div
            style={{
              display: 'flex',
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Loading />
          </div>
        )}
      </POSModal>
    ) : (
      <Fragment />
    );

  return (
    <>
      {content}
      <Toaster
        isOpen={toastState.isOpen}
        onClose={closeToast}
        type={toastState.type}
        title={toastState.title}
        message={toastState.message}
        duration={3000}
      />
    </>
  );
};

export default MobileDeliveryModal;
